import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { customInstance } from '@edison/api-client-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface EntryDetail {
  id: string;
  startTime: string;
  endTime: string;
  durationH: number;
  source: string;
  rdPercentage: number | null;
  commits: string[];
  sessionSummary: string | null;
  filesContext: string | null;
}

interface CalendarProject {
  projectId: string;
  projectName: string;
  rawHours: number;
  cappedHours: number;
  rdPct: number | null;
  entryCount: number;
}

interface CalendarPerson {
  userId: string;
  userName: string;
  rawHours: number;
  cappedHours: number;
  capRatio: number;
  projects: CalendarProject[];
}

interface CalendarDay {
  date: string;      // YYYY-MM-DD
  isWeekend: boolean;
  persons: CalendarPerson[];
}

interface CalendarData {
  month: string;     // YYYY-MM
  days: CalendarDay[];
  pendingByDate: Record<string, number>;  // date → pending+classifying entry count
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function currentIncomeYear(): string {
  const now = new Date();
  const m = now.getMonth(); // 0-indexed; 6 = July
  // Australian FY: July–June. FY label = end calendar year.
  // In July (first month of new FY), default to the previous completed FY —
  // the new FY just started and has little approved data yet.
  if (m === 6) return `FY${now.getFullYear()}`;
  return m >= 6 ? `FY${now.getFullYear() + 1}` : `FY${now.getFullYear()}`;
}

/** Returns the 12 YYYY-MM strings for a given FY (July → June). */
function fyMonths(incomeYear: string): string[] {
  const endYear = parseInt(incomeYear.slice(2), 10);
  const months: string[] = [];
  for (let m = 0; m < 12; m++) {
    const totalM = 6 + m; // July = index 6
    const year = (endYear - 1) + Math.floor(totalM / 12);
    const mo = (totalM % 12) + 1;
    months.push(`${year}-${String(mo).padStart(2, '0')}`);
  }
  return months;
}

/** Default display month: current calendar month if within FY, else last month of FY. */
function defaultMonth(incomeYear: string): string {
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const months = fyMonths(incomeYear);
  return months.includes(current) ? current : months[months.length - 1]!;
}

/** 10-colour palette — assigned to projects by first-appearance order. */
const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
];

/** Bar width = rawHours / BAR_BASELINE_HOURS. 12h baseline leaves room for overages. */
const BAR_BASELINE_HOURS = 12;
const DAILY_CAP_HOURS = 7.6;

function fmtH(h: number): string {
  return `${Math.round(h * 10) / 10}h`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

// ── EntryDrilldown ────────────────────────────────────────────────────────────

function EntryDrilldown({
  userId,
  date,
  projectId,
}: {
  userId: string;
  date: string;
  projectId: string;
}) {
  const [entries, setEntries] = useState<EntryDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    void customInstance<{ data: { entries: EntryDetail[] } }>({
      url: `/api/dashboard/labour-entries?userId=${userId}&date=${date}&projectId=${projectId}`,
      method: 'GET',
    }).then((res) => {
      if (live) setEntries(res.data.entries);
    }).catch(() => {
      if (live) setError(true);
    }).finally(() => {
      if (live) setLoading(false);
    });
    return () => { live = false; };
  }, [userId, date, projectId]);

  if (loading) {
    return <div className="py-2 text-xs text-muted-foreground animate-pulse">Loading entries…</div>;
  }
  if (error || !entries) {
    return <div className="py-2 text-xs text-destructive">Failed to load entries.</div>;
  }
  if (entries.length === 0) {
    return <div className="py-2 text-xs text-muted-foreground">No entries found.</div>;
  }

  return (
    <div className="mt-1 space-y-2 pl-3 border-l-2 border-border/40">
      {entries.map((e) => {
        const startD = new Date(e.startTime);
        const endD   = new Date(e.endTime);
        const startDateStr = startD.toISOString().slice(0, 10);
        const endDateStr   = endD.toISOString().slice(0, 10);
        const multiDay = startDateStr !== endDateStr;

        const fmtTime = (d: Date) => d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
        const fmtDayMonth = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });

        const startLabel = multiDay ? `${fmtDayMonth(startD)} ${fmtTime(startD)}` : fmtTime(startD);
        const endLabel   = multiDay ? `${fmtDayMonth(endD)} ${fmtTime(endD)}` : fmtTime(endD);

        // Flag entries > 16h — almost certainly a pipeline artifact (unclosed window)
        const suspicious = e.durationH > 16;

        return (
          <div key={e.id} className="text-xs space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`tabular-nums ${multiDay ? 'text-foreground' : 'text-muted-foreground'}`}>
                {startLabel}–{endLabel}
              </span>
              <span className={`tabular-nums font-medium ${suspicious ? 'text-amber-fg' : ''}`}>
                {fmtH(e.durationH)}
              </span>
              {suspicious && (
                <span className="text-amber-fg text-[10px]" title="Entry spans >16h — likely an unclosed CC sliding window. Check the raw entry.">
                  ⚠ abnormal span
                </span>
              )}
              <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                e.source === 'cc'
                  ? 'bg-violet-500/15 text-violet-400'
                  : 'bg-sky-500/15 text-sky-400'
              }`}>
                {e.source === 'cc' ? 'CC' : 'git'}
              </span>
              {e.rdPercentage !== null && (
                <span className="text-muted-foreground">{e.rdPercentage}% R&D</span>
              )}
            </div>
            {/* CC session context */}
            {e.sessionSummary && (
              <p className="text-muted-foreground leading-snug">{e.sessionSummary}</p>
            )}
            {e.filesContext && (
              <p className="text-muted-foreground/70 font-mono leading-snug truncate" title={e.filesContext}>
                {e.filesContext}
              </p>
            )}
            {/* GitHub commit messages */}
            {e.commits.length > 0 && (
              <ul className="text-muted-foreground space-y-0.5">
                {e.commits.slice(0, 3).map((msg, i) => (
                  <li key={i} className="truncate" title={msg}>· {msg}</li>
                ))}
                {e.commits.length > 3 && (
                  <li className="text-muted-foreground/60">+ {e.commits.length - 3} more</li>
                )}
              </ul>
            )}
            {e.commits.length === 0 && !e.sessionSummary && !e.filesContext && (
              <p className="text-muted-foreground/50">No context available</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── DayDetailPanel ───────────────────────────────────────────────────────────

function DayDetailPanel({
  selectedDate,
  day,
  projectColours,
  onClose,
}: {
  selectedDate: string | null;
  day: CalendarDay | null;
  projectColours: Map<string, string>;
  onClose: () => void;
}) {
  // expandedKey = `${userId}|${projectId}` when a project's entries are expanded
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Reset expanded state when the selected day changes
  useEffect(() => { setExpandedKey(null); }, [selectedDate]);

  return (
    <SlidePanel
      open={selectedDate !== null}
      onClose={onClose}
      title={selectedDate ? formatDate(selectedDate) : ''}
      width="w-[520px]"
    >
      <div className="p-5 space-y-6">
        {!day || day.persons.length === 0 ? (
          <p className="text-sm text-muted-foreground">No approved weekday time entries on this day.</p>
        ) : (
          day.persons.map((person) => (
            <div key={person.userId} className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{person.userName}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{fmtH(person.rawHours)} raw</span>
                  {person.capRatio < 1 ? (
                    <Badge variant="warning" className="text-xs">
                      capped → {fmtH(person.cappedHours)}
                    </Badge>
                  ) : (
                    <span className="text-xs text-emerald-600">{fmtH(person.cappedHours)}</span>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-2 text-[11px] font-medium text-muted-foreground border-b border-border pb-1">
                  <span>Project</span>
                  <span className="text-right">Raw</span>
                  <span className="text-right">Capped</span>
                  <span className="text-right">R&D%</span>
                  <span className="text-right">Entries</span>
                </div>
                {person.projects.map((proj) => {
                  const key = `${person.userId}|${proj.projectId}`;
                  const isExpanded = expandedKey === key;
                  return (
                    <div key={proj.projectId}>
                      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-2 items-center text-xs border-t border-border/40 pt-1">
                        <span className="inline-flex items-center gap-1.5 truncate">
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: projectColours.get(proj.projectId) ?? '#888' }}
                          />
                          {proj.projectName}
                        </span>
                        <span className="text-right tabular-nums">{fmtH(proj.rawHours)}</span>
                        <span className="text-right tabular-nums">{fmtH(proj.cappedHours)}</span>
                        <span className="text-right tabular-nums">
                          {proj.rdPct !== null ? `${proj.rdPct}%` : '—'}
                        </span>
                        <button
                          className={`text-right tabular-nums flex items-center justify-end gap-0.5 hover:text-foreground transition-colors ${
                            isExpanded ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                          onClick={() => setExpandedKey(isExpanded ? null : key)}
                          title="Show individual entries"
                        >
                          <span>{proj.entryCount}</span>
                          <span className="text-[10px]">{isExpanded ? '▾' : '▸'}</span>
                          {proj.entryCount > 5 && proj.rawHours < proj.entryCount * 0.5 && (
                            <span className="text-amber-fg" title="Many entries deduped — overlapping intervals collapsed">⚠</span>
                          )}
                        </button>
                      </div>
                      {isExpanded && selectedDate && (
                        <EntryDrilldown
                          userId={person.userId}
                          date={selectedDate}
                          projectId={proj.projectId}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {person.capRatio < 1 && (
                <p className="text-xs text-muted-foreground">
                  Cap ratio {Math.round(person.capRatio * 100)}% — raw {fmtH(person.rawHours)} exceeded 7.6h/day limit. Each project's hours scaled proportionally.
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </SlidePanel>
  );
}

// ── DayCell ──────────────────────────────────────────────────────────────────

function DayCell({
  date,
  day,
  isCurrentMonth,
  projectColours,
  pendingCount,
  onClick,
}: {
  date: string;
  day: CalendarDay | null;
  isCurrentMonth: boolean;
  projectColours: Map<string, string>;
  pendingCount: number;
  onClick: () => void;
}) {
  const dayNum = parseInt(date.slice(8), 10);
  const dow = new Date(date + 'T00:00:00Z').getUTCDay();
  const weekend = dow === 0 || dow === 6;
  const hasPersons = !weekend && (day?.persons.length ?? 0) > 0;
  const hasCap = hasPersons && (day?.persons.some((p) => p.capRatio < 1) ?? false);
  const hasPending = !weekend && pendingCount > 0;

  if (!isCurrentMonth) {
    return <div className="min-h-[80px] rounded bg-muted/10 opacity-20" />;
  }

  return (
    <div
      className={`min-h-[80px] rounded border p-1.5 text-xs transition-colors ${
        weekend
          ? 'bg-muted/20 border-border/20 opacity-50'
          : hasPersons
            ? 'bg-card border-border hover:border-primary/40 cursor-pointer'
            : hasPending
              ? 'bg-card border-amber-500/20'
              : 'bg-card border-border/40'
      }`}
      onClick={hasPersons ? onClick : undefined}
      role={hasPersons ? 'button' : undefined}
      tabIndex={hasPersons ? 0 : undefined}
      onKeyDown={hasPersons ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      {/* Date number + cap badge */}
      <div className="flex items-center justify-between mb-1">
        <span className={`font-medium text-[11px] ${weekend ? 'text-muted-foreground/50' : 'text-foreground'}`}>
          {dayNum}
        </span>
        {hasCap && (
          <span className="text-amber-fg text-[9px] font-semibold leading-none">⚠</span>
        )}
      </div>

      {weekend && (
        <span className="text-[9px] text-muted-foreground/40">weekend</span>
      )}

      {/* Pending indicator — shown whenever there are unreviewed entries on this day,
          even if some people already have approved bars. This surfaces the "hidden team
          members" case where A is approved but B/C/D are still pending. */}
      {hasPending && (
        <div className="text-[9px] text-amber-fg/80 flex items-center gap-0.5 mt-0.5" title={`${pendingCount} entries awaiting review`}>
          <span>⏳</span>
          <span>{pendingCount} pending</span>
        </div>
      )}

      {/* Per-person bars with initials */}
      {hasPersons && day && day.persons.map((person) => {
        const barWidthPct = Math.min(person.rawHours / BAR_BASELINE_HOURS, 1) * 100;
        const capLinePct = person.capRatio < 1
          ? (DAILY_CAP_HOURS / BAR_BASELINE_HOURS) * 100
          : null;

        return (
          <div key={person.userId} className="mb-1.5 last:mb-0 flex items-start gap-1">
            {/* Initials avatar */}
            <span
              className="shrink-0 w-[14px] h-[14px] rounded-full bg-muted flex items-center justify-center text-[8px] font-semibold leading-none text-muted-foreground mt-0.5"
              title={person.userName}
            >
              {initials(person.userName)}
            </span>
            {/* Bar + chips */}
            <div className="flex-1 min-w-0">
              {/* Segmented progress bar */}
              <div className="relative h-[8px] rounded-full bg-muted/40 overflow-hidden mb-0.5">
                <div
                  className="absolute inset-y-0 left-0 flex overflow-hidden rounded-full"
                  style={{ width: `${barWidthPct}%` }}
                >
                  {person.projects.map((proj, i) => {
                    const segPct = person.rawHours > 0
                      ? (proj.rawHours / person.rawHours) * 100
                      : 0;
                    return (
                      <div
                        key={proj.projectId}
                        style={{
                          width: `${segPct}%`,
                          background: projectColours.get(proj.projectId) ?? PALETTE[i % PALETTE.length]!,
                        }}
                      />
                    );
                  })}
                </div>
                {capLinePct !== null && (
                  <div
                    className="absolute inset-y-0 bg-background/70"
                    style={{ left: `${capLinePct}%`, right: 0 }}
                  />
                )}
                {capLinePct !== null && (
                  <div
                    className="absolute inset-y-0 w-[2px] bg-amber-500"
                    style={{ left: `${capLinePct}%` }}
                  />
                )}
              </div>
              {/* Project chips */}
              <div className="flex flex-wrap gap-0.5">
                {person.projects.map((proj, i) => {
                  const colour = projectColours.get(proj.projectId) ?? PALETTE[i % PALETTE.length]!;
                  return (
                    <span
                      key={proj.projectId}
                      className="text-[8px] leading-tight px-1 py-0.5 rounded"
                      style={{ background: colour + '22', color: colour }}
                    >
                      {proj.projectName.split(' ')[0]} {fmtH(proj.rawHours)}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── CalendarView ─────────────────────────────────────────────────────────────

export function CalendarView() {
  const [incomeYear, setIncomeYear] = useState<string>(currentIncomeYear);
  const [month, setMonth] = useState<string>(() => defaultMonth(currentIncomeYear()));
  const [includeNoRd, setIncludeNoRd] = useState(false);
  const [data, setData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const months = useMemo(() => fyMonths(incomeYear), [incomeYear]);
  const monthIdx = months.indexOf(month);

  const fetchCalendar = useCallback(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ incomeYear, month });
    if (includeNoRd) params.set('includeNoRd', 'true');
    void customInstance<{ data: CalendarData }>({
      url: `/api/dashboard/labour-calendar?${params.toString()}`,
      method: 'GET',
    }).then((res) => {
      if (live) setData(res.data);
    }).catch(() => {
      if (live) setError('Failed to load calendar data. Please try again.');
    }).finally(() => {
      if (live) setLoading(false);
    });
    return () => { live = false; };
  }, [incomeYear, month, includeNoRd]);

  useEffect(() => {
    const cleanup = fetchCalendar();
    return cleanup;
  }, [fetchCalendar]);

  // Assign colours to projects by first-appearance order across all days
  const projectColours = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const day of data.days) {
      for (const person of day.persons) {
        for (const proj of person.projects) {
          if (!map.has(proj.projectId)) {
            map.set(proj.projectId, PALETTE[map.size % PALETTE.length]!);
          }
        }
      }
    }
    return map;
  }, [data]);

  const dayByDate = useMemo(() => {
    const map = new Map<string, CalendarDay>();
    if (!data) return map;
    for (const day of data.days) map.set(day.date, day);
    return map;
  }, [data]);

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const day of data.days) {
      for (const person of day.persons) {
        for (const proj of person.projects) {
          if (!map.has(proj.projectId)) map.set(proj.projectId, proj.projectName);
        }
      }
    }
    return map;
  }, [data]);

  // Build the 7-column grid: Mon-based weeks spanning the full display month
  const gridDates = useMemo(() => {
    const [yStr, mStr] = month.split('-') as [string, string];
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    const firstDay = new Date(Date.UTC(y, m - 1, 1));
    const lastDay = new Date(Date.UTC(y, m, 0));

    // Start on the Monday on or before the 1st
    const dow = firstDay.getUTCDay(); // 0=Sun … 6=Sat
    const startOffset = dow === 0 ? -6 : 1 - dow;
    const gridStart = new Date(firstDay.getTime() + startOffset * 86_400_000);

    // End on the Sunday on or after the last day
    const endDow = lastDay.getUTCDay();
    const endOffset = endDow === 0 ? 0 : 7 - endDow;
    const gridEnd = new Date(lastDay.getTime() + endOffset * 86_400_000);

    const dates: string[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
  }, [month]);

  const selectedDay = selectedDate ? (dayByDate.get(selectedDate) ?? null) : null;

  // FY options: current year ±2
  const nowYear = new Date().getFullYear();
  const fyOptions = Array.from({ length: 4 }, (_, i) => `FY${nowYear - 1 + i}`);

  const [yStr, mStr] = month.split('-') as [string, string];
  const monthLabel = new Date(Date.UTC(parseInt(yStr, 10), parseInt(mStr, 10) - 1, 1))
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <select
            value={incomeYear}
            onChange={(e) => {
              const fy = e.target.value;
              setIncomeYear(fy);
              setMonth(defaultMonth(fy));
              setSelectedDate(null);
            }}
            className="text-sm border rounded px-2 py-1.5 bg-background"
            aria-label="Income year"
          >
            {fyOptions.map((fy) => <option key={fy} value={fy}>{fy}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeNoRd}
              onChange={(e) => { setIncludeNoRd(e.target.checked); setSelectedDate(null); }}
              className="rounded"
            />
            All projects
          </label>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary" size="sm"
            disabled={monthIdx <= 0}
            onClick={() => { setMonth(months[monthIdx - 1]!); setSelectedDate(null); }}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[150px] text-center">{monthLabel}</span>
          <Button
            variant="secondary" size="sm"
            disabled={monthIdx >= months.length - 1}
            onClick={() => { setMonth(months[monthIdx + 1]!); setSelectedDate(null); }}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-1 text-[11px] font-medium text-muted-foreground text-center">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="py-1">{d}</div>
        ))}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="min-h-[80px] rounded skeleton" />
          ))}
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchCalendar}>
            Retry
          </Button>
        </div>
      )}

      {/* Calendar grid */}
      {!loading && !error && (
        <div className="grid grid-cols-7 gap-1">
          {gridDates.map((date) => (
            <DayCell
              key={date}
              date={date}
              day={dayByDate.get(date) ?? null}
              isCurrentMonth={date.slice(0, 7) === month}
              projectColours={projectColours}
              pendingCount={data?.pendingByDate[date] ?? 0}
              onClick={() => setSelectedDate(date)}
            />
          ))}
        </div>
      )}

      {/* Project colour legend */}
      {!loading && !error && projectColours.size > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-3 border-t">
          {[...projectColours.entries()].map(([projectId, colour]) => {
            const name = projectNameById.get(projectId) ?? projectId;
            return (
              <span key={projectId} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colour }} />
                {name}
              </span>
            );
          })}
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-[2px] h-3 bg-amber-500 rounded-full shrink-0" />
            7.6h cap line
          </span>
        </div>
      )}

      {/* Day detail panel */}
      <DayDetailPanel
        selectedDate={selectedDate}
        day={selectedDay}
        projectColours={projectColours}
        onClose={() => setSelectedDate(null)}
      />
    </div>
  );
}
